import { Check, ExternalLink } from "lucide-react";

type Props = {
  connection: { livemode: boolean; restricted: boolean } | null;
};

export function StripeConnection({ connection }: Props) {
  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">Stripe</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            This app uses your Stripe account via the{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              STRIPE_SECRET_KEY
            </code>{" "}
            in the server environment (a standard secret key{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">sk_</code> or a
            restricted key <code className="rounded bg-muted px-1 py-0.5 text-xs">rk_</code>
            ). There is no Stripe Connect OAuth — the key always refers to your
            own account.
          </p>
        </div>
      </div>

      {connection ? (
        <div className="flex items-center justify-between rounded-md border bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Check className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-medium">
                {connection.restricted ? "Restricted key" : "Secret key"} configured
              </div>
              <div className="text-xs text-muted-foreground">
                {connection.livemode ? "Live mode" : "Test mode"} (
                {connection.restricted
                  ? connection.livemode
                    ? "rk_live_"
                    : "rk_test_"
                  : connection.livemode
                    ? "sk_live_"
                    : "sk_test_"}
                …)
              </div>
            </div>
          </div>
          <a
            href="https://dashboard.stripe.com/apikeys"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-md border bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
          >
            API keys
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      ) : (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">Add a secret or restricted key</p>
          <p className="mt-1 text-amber-800/90">
            Set <code className="rounded bg-amber-100/80 px-1">STRIPE_SECRET_KEY</code> in{" "}
            <code className="rounded bg-amber-100/80 px-1">server/.env</code> (or{" "}
            <code className="rounded bg-amber-100/80 px-1">server/.env.local</code>
            ) to a standard key (
            <code className="rounded bg-amber-100/80 px-1">sk_</code>) or restricted key (
            <code className="rounded bg-amber-100/80 px-1">rk_</code>) from{" "}
            <a
              className="font-medium underline"
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe Dashboard → API keys
            </a>
            . The restricted key must allow the resources this app uses (products,
            subscriptions, customers, etc.). Restart the server after changing the
            file.
          </p>
        </div>
      )}
    </div>
  );
}
